import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Caption from '../xocs/caption/caption';
import FigureTitle from './figureTitle';
import PanelHeader from '../sidebar/panelHeader/panelHeader';
import PanelPaginationControls from '../sidebar/panelHeader/panelPaginationControls';
import ReferenceRange from '../references/referenceRange';
import getPdfViewerApplication from '../../utils/pdfjs/pdfViewerApplication';
import { createAssetLink } from '../../utils/linkCreator';
import { referenceType as referenceTypeConstant } from '../../../shared/constants';
import DownloadLink from './figureDownloadLink';

const referenceType = referenceTypeConstant.figure;
const query = { referenceType };

const { enhancementManager } = getPdfViewerApplication();
const getActiveReference = () => enhancementManager.getActiveReference(query);
const getPaginationInfo = () => enhancementManager.getReferencesPaginationInfo(referenceType);

export default function Figure() {
  const [{ reference, labels }, setActiveReference] = useState(getActiveReference());

  const [positionAtLocation, setPositionAtLocation] = useState(0);

  const {
    hasNext,
    hasPrevious,
    totalItems,
    activeIndex,
  } = getPaginationInfo();

  const goToNext = useCallback(async () => {
    await enhancementManager.goToNextReference(referenceType);
    setActiveReference(getActiveReference());
  }, [setActiveReference]);

  const goToPrev = useCallback(async () => {
    await enhancementManager.goToPrevReference(referenceType);
    setActiveReference(getActiveReference());
  }, [setActiveReference]);

  const selectInRange = useCallback(async (position) => {
    setPositionAtLocation(position);
    await enhancementManager.goToReferenceAtLocation(referenceType, null, null, position);
    setActiveReference(getActiveReference());
  }, [setPositionAtLocation]);

  const scrollPanelRef = useRef(null);

  useEffect(() => {
    scrollPanelRef.current.scrollTop = 0;
  });

  const {
    caption, label, figure: { regular: regularFigure, large: largeFigure, altText },
  } = reference;

  const labelToShow = labels.length > 1
    ? `${labels[0].replace(']', '')}-${labels[labels.length - 1].replace('[', '')}`
    : label;

  return (
    <div className="sidebar-scroll-panel sidebar-panel-default-appearance figure-panel">
      <PanelHeader title={<FigureTitle activeIndex={activeIndex} totalItems={totalItems} />}>
        <PanelPaginationControls
          hasNext={hasNext}
          hasPrev={hasPrevious}
          goToNext={goToNext}
          goToPrev={goToPrev}
        />
      </PanelHeader>
      <div
        className="sidebar-scroll-panel-body"
        id="figure-panel"
        ref={scrollPanelRef}
      >
        <ReferenceRange
          labels={labels}
          currentlyViewedIndex={positionAtLocation}
          selectInRange={selectInRange}
        />
        <figure className="figure-panel-body">
          <img
            alt={altText}
            src={createAssetLink(regularFigure)}
          />
          <figcaption className="u-margin-s-top u-font-serif">
            <span className="figure-panel-label">{`${labelToShow} `}</span>
            {caption && <Caption content={caption} />}
          </figcaption>
        </figure>
        <div className="u-font-sans">
          <DownloadLink href={createAssetLink(regularFigure)} downloadText="Download image" />
          {largeFigure && (<DownloadLink href={createAssetLink(largeFigure)} downloadText="Download high resolution image" />)}
        </div>
      </div>
    </div>
  );
}
